import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { BottomNav } from '@/components/BottomNav';
import { ConnectionStatus } from '@/components/ConnectionStatus';
import { Prefetch } from '@/components/Prefetch';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { useSession } from '@/features/auth/useSession';
import { ConsentScreen } from '@/features/consent/ConsentScreen';
import { useConsent } from '@/features/consent/useConsent';
import { Onboarding } from '@/features/onboarding/Onboarding';
import { useProfileState } from '@/features/profile/useProfile';
import { useSyncLoop } from '@/lib/sync/useSync';
import { useTheme } from '@/lib/useTheme';

import Today from '@/routes/Today';
import Add from '@/routes/Add';
import WorkoutPage from '@/routes/Workout';
import ProfilePage from '@/routes/Profile';
import NotFound from '@/routes/NotFound';

// Progreso arrastra Recharts (~100 KB). Es la pantalla que menos se abre, así
// que no debe pesar en el arranque de las que se abren a diario.
const Progress = lazy(() => import('@/routes/Progress'));

export default function App() {
  // Todos los hooks arriba y sin condiciones: las puertas de abajo cambian lo
  // que se pinta, nunca cuántos hooks se ejecutan.
  useSyncLoop();
  useTheme();
  const { session, loading, localOnly } = useSession();
  const consent = useConsent();
  const profile = useProfileState();

  if (loading || consent === 'cargando') return <Splash />;
  if (!localOnly && !session) return <LoginScreen />;
  if (consent === 'pendiente') return <ConsentScreen />;
  if (profile.status === 'cargando') return <Splash />;
  if (profile.status === 'sin_perfil') return <Onboarding />;
  if (profile.status === 'invalido') return <Onboarding initial={profile.profile} fixing />;

  return (
    <div className="min-h-dvh pb-[calc(4rem+env(safe-area-inset-bottom,0px))]">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-[var(--color-surface)] focus:px-3 focus:py-2"
      >
        Saltar al contenido
      </a>

      <ConnectionStatus />
      {/* Va aquí dentro, detrás de las puertas: no se baja nada para quien
          todavía no ha entrado. */}
      <Prefetch />

      <main id="contenido">
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/anadir" element={<Add />} />
          <Route path="/entreno" element={<WorkoutPage />} />
          <Route
            path="/progreso"
            element={
              <Suspense
                fallback={
                  <p className="p-4 text-sm text-[var(--color-text-muted)]">Cargando gráficos…</p>
                }
              >
                <Progress />
              </Suspense>
            }
          />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <UpdatePrompt />
      <BottomNav />
    </div>
  );
}

/** Pantalla de espera breve. Sin spinner: parpadearía más de lo que informa. */
function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <span className="sr-only">Cargando NutriGym</span>
    </div>
  );
}
